import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Flex, Heading, Text, Button, IconButton, useToast, useDisclosure, HStack,
  Menu, MenuButton, MenuList, MenuItem, Badge, Tooltip, InputGroup, InputLeftElement, Icon,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, Input, FormControl, FormLabel, VStack, Select, Divider, Checkbox,
} from '@chakra-ui/react';
import { FiAlertCircle, FiLogOut, FiSettings, FiUser, FiChevronDown, FiMail, FiUsers, FiBriefcase, FiSearch, FiMessageSquare,
  FiCalendar, FiPackage, FiEye, FiActivity, FiUserPlus, FiSliders, FiGrid, FiTruck, FiDollarSign, FiColumns, FiShoppingCart, FiBookOpen, FiTrendingUp, FiCheckSquare, FiClock, FiList, FiRefreshCw, FiCreditCard, FiClipboard, FiKey, FiUpload, FiPercent } from 'react-icons/fi';
import IncidentDashboard from '../components/IncidentDashboard';
import PowerDashMenu from '../components/PowerDashMenu';
import { APP_VERSION } from '../version';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import MainGrid from '../components/MainGrid';
import SettingsDrawer from '../components/SettingsDrawer';
import EngagementModal from '../components/EngagementModal';
import ContactsListModal from '../components/ContactsListModal';
import CompaniesListModal from '../components/CompaniesListModal';
import CalendarDashboardModal from '../components/CalendarDashboardModal';
import ProductsModal from '../components/ProductsModal';
import ProspectingDashboard from '../components/ProspectingDashboard';
import InboxModal from '../components/InboxModal';
import ViewSelectorModal from '../components/ViewSelectorModal';
import EngagementFilterModal from '../components/EngagementFilterModal';
import AccountingModal from '../components/AccountingModal';
import MileageReportsModal from '../components/MileageReportsModal';
import AlarmPopup from '../components/AlarmPopup';
import DashboardColumnsModal from '../components/DashboardColumnsModal';
import DepartmentsModal from '../components/DepartmentsModal';
import ResourcesModal from '../components/ResourcesModal';
import SupplierProductsModal from '../components/SupplierProductsModal';
import ResourceBookingsDashboard from '../components/ResourceBookingsDashboard';
import SiteVisitorsDashboard from '../components/SiteVisitorsDashboard';
import BookMyDiaryDashboard from '../components/BookMyDiaryDashboard';
import BusinessPipelineDashboard from '../components/BusinessPipelineDashboard';
import ToDoListsDashboard from '../components/ToDoListsDashboard';
import ToDoListPage from '../components/ToDoListPage';
import BankLedgerDashboard from '../components/BankLedgerDashboard';
import HolidayDashboard from '../components/HolidayDashboard';
import WorkSessionBillingDashboard from '../components/WorkSessionBillingDashboard';
import WorkSessionModal from '../components/WorkSessionModal';
import TimesheetsDashboard from '../components/TimesheetsDashboard';
import BookKeeperEntryModal from '../components/BookKeeperEntryModal';
import AssignReceivedMoneyModal from '../components/AssignReceivedMoneyModal';
import ProceduresDashboard from '../components/ProceduresDashboard';
import InvoiceIntakeDashboard from '../components/InvoiceIntakeDashboard';
import VatDashboard from '../components/VatDashboard';
import { navigate, usePath, pageForPath } from '../nav';
import UploadPortalsPage from '../components/UploadPortalsPage';
import PasswordSafeDashboard from '../components/PasswordSafeDashboard';
import ProspectsModal from '../components/ProspectsModal';
import SubscriptionsDashboard from '../components/SubscriptionsDashboard';
import ReferralsDashboard from '../components/ReferralsDashboard';
import NationalHolidaysModal from '../components/NationalHolidaysModal';
import NominalCodesModal from '../components/NominalCodesModal';
import SalaryDashboard from '../components/SalaryDashboard';
import PensionDashboard from '../components/PensionDashboard';
import EmployeeTaxDashboard from '../components/EmployeeTaxDashboard';
import PaymentCardsPage from '../components/PaymentCardsPage';
import ProjectFocusDashboard from '../components/ProjectFocusDashboard';
import EmailFiltersDashboard from '../components/EmailFiltersDashboard';

export default function Dashboard() {
  const { user, logout, textScale, updateTextScale } = useAuth();
  const feat = user?.features || {};
  const hasFeature = (f) => feat[f] !== false;
  const toast = useToast();
  const settingsDisclosure = useDisclosure();
  const engagementDisclosure = useDisclosure();
  const contactsDisclosure = useDisclosure();
  const companiesDisclosure = useDisclosure();
  const calendarDashDisclosure = useDisclosure();
  const inboxDisclosure = useDisclosure();
  const viewSelectorDisclosure = useDisclosure();
  const engFilterDisclosure = useDisclosure();
  const dateFilterDisclosure = useDisclosure();
  const columnsDisclosure = useDisclosure();
  const departmentsDisclosure = useDisclosure();
  const resourcesDisclosure = useDisclosure();
  const bookingsDashDisclosure = useDisclosure();
  const siteVisitorsDisclosure = useDisclosure();
  const bookDiaryDisclosure = useDisclosure();
  const todoDisclosure = useDisclosure();
  const bankLedgerDisclosure = useDisclosure();
  const holidayDisclosure = useDisclosure();
  const timesheetsDisclosure = useDisclosure();
  const bookKeeperEntryDisclosure = useDisclosure();
  const assignMoneyDisclosure = useDisclosure();
  // Deep link into the Procedures PAGE (proper page, not a modal) from
  // anywhere in the app — e.g. the task modal's Edit pencil. Carrying it
  // via a window event avoids prop-drilling through the grid tree.
  const [procDeepLink, setProcDeepLink] = useState(null); // { editId? , viewId? }
  useEffect(() => {
    const handler = (e) => {
      setProcDeepLink(e.detail || null);
      setCurrentPage('procedures');
    };
    window.addEventListener('forgebos:open-procedures', handler);
    return () => window.removeEventListener('forgebos:open-procedures', handler);
  }, []);

  // Client Upload Portals page (cloud icon on tasks/sub-tasks).
  const [uploadPortalCtx, setUploadPortalCtx] = useState(null); // { taskId? , subTaskId?, name }
  useEffect(() => {
    const h = (e) => { setUploadPortalCtx(e.detail || null); setCurrentPage('upload-portals'); };
    window.addEventListener('forgebos:open-upload-portals', h);
    return () => window.removeEventListener('forgebos:open-upload-portals', h);
  }, []);
  const nationalHolidaysDisclosure = useDisclosure();

  const [bookKeeperDir, setBookKeeperDir] = React.useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [priorityMin, setPriorityMin] = useState(0);
  const [priorityMax, setPriorityMax] = useState(10);
  const [scheduleMenu, setScheduleMenu] = useState(null);
  const [intakeTarget, setIntakeTarget] = useState(null);   // task opened from the grid's red £
  const feedbackDashDisclosure = useDisclosure();
  const incidentDashDisclosure = useDisclosure();
  const emailFiltersDisclosure = useDisclosure();
  const [alarmMode, setAlarmMode] = useState('calendar_only');
  const prospectsDisclosure = useDisclosure();
  const subscriptionsDisclosure = useDisclosure();
  const referralsDisclosure = useDisclosure();
  const path = usePath();
  const [currentPage, setCurrentPageState] = useState(() => pageForPath(window.location.pathname));
  // Routed pages live at their own URL; everything else stays in page state.
  useEffect(() => {
    const p = pageForPath(window.location.pathname);
    if (p) setCurrentPageState(p);
    else setCurrentPageState(prev => (prev === 'invoiceIntake' || prev === 'vat') ? null : prev);
  }, [path]);
  const setCurrentPage = (p) => {
    if (p === 'invoiceIntake') { navigate('/accounts/invoices'); return; }
    if (p === 'vat') { navigate('/accounts/vat'); return; }
    if (p === null && pageForPath(window.location.pathname)) { navigate('/'); return; }
    setCurrentPageState(p);
  };

  // ── URL routing: keep /prospecting and /todo paths in sync with `currentPage` ──
  // The TODO list has its own URL so the user can bookmark / share / refresh
  // straight to it. Same pattern as /prospecting — when the path is on /todo,
  // we render the full-page ToDoListPage; on back-button / manual change away,
  // we clear currentPage.
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.startsWith('/prospecting')) {
      if (currentPage !== 'prospecting') setCurrentPage('prospecting');
    } else if (location.pathname.startsWith('/todo')) {
      if (currentPage !== 'todo') setCurrentPage('todo');
    } else if (currentPage === 'prospecting' || currentPage === 'todo') {
      // User navigated away (back button or manual URL change)
      setCurrentPage(null);
    }
  // eslint-disable-next-line
  }, [location.pathname]);
  const [globalActiveSession, setGlobalActiveSession] = useState(null);
  const [globalElapsed, setGlobalElapsed] = useState(0);
  const [floatingTimerTask, setFloatingTimerTask] = useState(null);
  const globalTimerRef = React.useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchScope, setSearchScope] = useState('projects');
  const [searchAllRows, setSearchAllRows] = useState(true);
  const [showDeletedProjects, setShowDeletedProjects] = useState(false);

  const [projects, setProjects] = useState([]);
  const [rowGroups, setRowGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewFilters, setViewFilters] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [activeViewName, setActiveViewName] = useState('Default');
  const activeFilterRef = React.useRef(null);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [taskUnread, setTaskUnread] = useState({});
  const [highlightTaskId, setHighlightTaskId] = useState(null);
  const [autoOpenComms, setAutoOpenComms] = useState(null);
  const [engagementFilter, setEngagementFilter] = useState([]);

  // Per-project "Show Deleted & Completed Tasks" reveal (migration 165):
  // the grid's project context menu dispatches an event with the project
  // id (or null to hide again); the projects fetch carries it so hidden
  // (do_not_show) tasks return for that one project.
  const [showHiddenTasksProject, setShowHiddenTasksProject] = useState(null);
  useEffect(() => {
    const h = (e) => setShowHiddenTasksProject(e.detail?.projectId || null);
    window.addEventListener('forgebos:show-hidden-tasks', h);
    return () => window.removeEventListener('forgebos:show-hidden-tasks', h);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const qp = [];
      if (showDeletedProjects) qp.push('include_deleted=true');
      if (showHiddenTasksProject) qp.push(`show_hidden_tasks_project=${showHiddenTasksProject}`);
      const projParams = qp.length ? `?${qp.join('&')}` : '';
      const [projRes, groupRes] = await Promise.all([
        api.get(`/projects${projParams}`),
        api.get('/row-groups'),
      ]);
      setProjects(projRes.data);
      // Re-apply the active view filter to the freshly-fetched row groups.
      // After an action (add/delete/edit) triggers this refetch, we need
      // to preserve whatever the user had selected from the views dropdown:
      //   - a real view_filter object  → apply its visibility map
      //   - the '__show_all__' sentinel → force every group visible
      //   - null                       → leave groups at server defaults
      //
      // The fallback chain config[g.id] ?? config[g._id] mirrors the bridge
      // in applyViewFilter: filter_config keys can be either UUIDs or legacy
      // integer strings, and rowGroups.js intentionally re-adds _id (integer)
      // for this exact lookup. Previously fetchData only used g.id, so any
      // saved view with integer keys silently fell through to defaultVis on
      // refetch — manifesting as "the view resets after every action".
      const filter = activeFilterRef.current;
      if (filter === '__show_all__') {
        setRowGroups(groupRes.data.map(g => ({ ...g, visibility: 'show', visible: true })));
      } else if (filter) {
        let config = {};
        try { config = typeof filter.filter_config === 'string' ? JSON.parse(filter.filter_config) : (filter.filter_config || {}); } catch(e) {}
        const defaultVis = config.default || 'show';
        setRowGroups(groupRes.data.map(g => {
          const vis = config[g.id] ?? config[g._id] ?? defaultVis;
          return { ...g, visibility: vis, visible: vis !== 'hide' };
        }));
      } else {
        setRowGroups(groupRes.data);
      }
    } catch (err) {
      toast({ title: 'Failed to load data', status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [toast, showDeletedProjects, showHiddenTasksProject]);

  const fetchViewFilters = useCallback(async () => {
    try {
      const { data } = await api.get('/view-filters');
      setViewFilters(data);
    } catch(e) {}
  }, []);

  const fetchUnreadCounts = useCallback(async () => {
    try {
      const { data } = await api.get('/email/unread-counts');
      setInboxUnread(data.inbox_unread || 0);
      setTaskUnread(data.task_unread || {});
    } catch(e) {}
  }, []);

  useEffect(() => {
    fetchData();
    fetchViewFilters();
    fetchUnreadCounts();
    // Poll for new emails every 60 seconds
    const interval = setInterval(fetchUnreadCounts, 60000);
    return () => clearInterval(interval);
  }, [fetchData, fetchViewFilters, fetchUnreadCounts]);

  // Global active work session polling
  useEffect(() => {
    const pollSession = async () => {
      try {
        const { data } = await api.get('/work-sessions/active');
        setGlobalActiveSession(data || null);
        if (data) {
          const start = new Date(data.start_time).getTime();
          setGlobalElapsed(Math.floor((Date.now() - start) / 1000));
        } else {
          setGlobalElapsed(0);
        }
      } catch(e) { setGlobalActiveSession(null); }
    };
    pollSession();
    const interval = setInterval(pollSession, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  // Tick the elapsed counter every second when a session is active
  useEffect(() => {
    if (globalActiveSession) {
      globalTimerRef.current = setInterval(() => {
        const start = new Date(globalActiveSession.start_time).getTime();
        setGlobalElapsed(Math.floor((Date.now() - start) / 1000));
      }, 1000);
    } else {
      if (globalTimerRef.current) clearInterval(globalTimerRef.current);
    }
    return () => { if (globalTimerRef.current) clearInterval(globalTimerRef.current); };
  }, [globalActiveSession]);

  // Restore active filter from session or user default on first mount
  useEffect(() => {
    if (viewFilters.length > 0 && !activeViewId) {
      // Try to restore last active filter from sessionStorage
      let restored = false;
      try {
        const savedId = sessionStorage.getItem('forgebos_active_filter_id');
        if (savedId === '__show_all__') {
          // Restore the "Show All" sentinel — same path as the ViewSelector onApply.
          activeFilterRef.current = '__show_all__';
          setRowGroups(prev => prev.map(g => ({ ...g, visibility: 'show', visible: true })));
          setActiveViewId('__show_all__');
          setActiveViewName('Show All');
          restored = true;
        } else if (savedId) {
          const f = viewFilters.find(v => v.id === savedId);
          if (f) { applyViewFilter(f); restored = true; }
        }
      } catch(e) {}
      // Fall back to user default
      if (!restored && user?.default_view_filter_id) {
        const dv = viewFilters.find(v => v.id === user.default_view_filter_id);
        if (dv) applyViewFilter(dv);
      }
    }
  }, [user, viewFilters]); // eslint-disable-line

  // Load user's priority filter prefs
  useEffect(() => {
    if (user) {
      if (user.filter_priority_min !== undefined) setPriorityMin(user.filter_priority_min);
      if (user.filter_priority_max !== undefined) setPriorityMax(user.filter_priority_max);
      if (user.alarm_mode) setAlarmMode(user.alarm_mode);
    }
  }, [user]);

  // Close schedule menu on click
  useEffect(() => {
    const h = () => setScheduleMenu(null);
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, []);

  const changeAlarmMode = async (mode) => {
    setAlarmMode(mode);
    setScheduleMenu(null);
    try { await api.put('/auth/preferences', { alarm_mode: mode }); } catch(e) {}
  };

  const applyViewFilter = (filter) => {
    if (!filter) return;
    activeFilterRef.current = filter;
    let config = {};
    try { config = typeof filter.filter_config === 'string' ? JSON.parse(filter.filter_config) : (filter.filter_config || {}); } catch(e) {}
    const defaultVis = config.default || 'show';
    setRowGroups(prev => prev.map(g => ({
      ...g,
      // config keys are integer strings (e.g. "5"); g.id is UUID, g._id is integer
      visibility: config[g.id] ?? config[g._id] ?? defaultVis,
      visible: (config[g.id] ?? config[g._id] ?? defaultVis) !== 'hide',
    })));
    setActiveViewId(filter.id);
    setActiveViewName(filter.name);
    // Persist across page loads
    try { sessionStorage.setItem('forgebos_active_filter_id', String(filter.id)); } catch(e) {}
  };

  const handleViewChange = (filterId) => {
    if (!filterId) { activeFilterRef.current = null; fetchData(); setActiveViewId(null); setActiveViewName('Default'); try { sessionStorage.removeItem('forgebos_active_filter_id'); } catch(e) {} return; }
    const f = viewFilters.find(v => v.id === filterId);
    if (f) applyViewFilter(f);
  };

  const handleAddProject = async (rowGroupId) => {
    try {
      const { data } = await api.post('/projects', {
        project_name: '',
        row_group_id: rowGroupId,
        commissioning_date: new Date().toISOString().split('T')[0],
      });
      // Optimistically prepend to the group so the header never re-mounts
      const newProject = {
        ...data,
        row_group_id: rowGroupId,
        tasks: [],
        comm_count: 0, notes_count: 0, inv_out_count: 0, inv_in_count: 0,
        calendar_count: 0, supplier_count: 0,
      };
      setProjects(prev => [newProject, ...prev]);
      // Background sync to pick up server-generated fields
      fetchData();
      return data;
    } catch (err) {
      toast({ title: 'Failed to add row', status: 'error', duration: 3000 });
    }
  };

  const handleUpdateProject = async (id, updates) => {
    try {
      // Optimistic local update — avoids full re-fetch that resets inline editors
      setProjects(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
      await api.put(`/projects/${id}`, updates);
    } catch (err) {
      toast({ title: 'Failed to update', status: 'error', duration: 3000 });
      // Revert on failure
      fetchData();
    }
  };

  const handleDeleteProject = async (id, reason) => {
    try {
      await api.delete(`/projects/${id}`, { data: { reason } });
      await fetchData();
      toast({ title: 'Project deleted', status: 'info', duration: 1500 });
    } catch (err) {
      toast({ title: 'Failed to delete', status: 'error', duration: 3000 });
    }
  };

  const visibleGroups = rowGroups.filter(g => g.visibility !== 'hide');

  // Apply engagement filter
  let filteredProjects = engagementFilter.length > 0
    ? projects.filter(p => p.engagement_status_id && engagementFilter.includes(p.engagement_status_id))
    : projects;

  // Apply date filter
  if (dateFrom || dateTo) {
    filteredProjects = filteredProjects.filter(p => {
      const pDate = p.created_at ? p.created_at.split('T')[0] : null;
      if (!pDate) return true;
      if (dateFrom && pDate < dateFrom) return false;
      if (dateTo && pDate > dateTo) return false;
      return true;
    });
  }

  // Apply priority filter
  if (priorityMin > 0 || priorityMax < 10) {
    filteredProjects = filteredProjects.filter(p => {
      const pl = p.priority_level ?? 5;
      return pl >= priorityMin && pl <= priorityMax;
    });
  }

  const hasActiveFilters = dateFrom || priorityMin > 0 || priorityMax < 10;

  // Apply search filter
  if (searchQuery.trim()) {
    const q = searchQuery.trim().toLowerCase();
    const doneGroupIds = rowGroups.filter(g => /done|delivered|paid|closed|cancel/i.test(g.name)).map(g => g.id);

    // When searchAllRows=true (default), search ALL projects regardless of current view filter
    // When searchAllRows=false (filtered mode), only search within the current visible row groups
    const searchBase = searchAllRows ? projects : filteredProjects;

    filteredProjects = searchBase.filter(proj => {
      // When filtering to row groups only, restrict to visible groups
      if (!searchAllRows && !visibleGroups.some(g => g.id === proj.row_group_id)) return false;

      const projName = (proj.project_name || '').toLowerCase();
      // company_name is a direct field; companies[] may also exist if multi-company
      const projCompanies = [(proj.company_name || ''), ...(proj.companies || []).map(c => c.name || '')].map(s => s.toLowerCase()).filter(Boolean);
      const projContacts = (proj.contacts || []).map(c => `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase());
      const tasks = proj.tasks || [];
      const taskTitles = tasks.map(t => (t.task_title || '').toLowerCase());
      const taskCompanies = tasks.flatMap(t => [(t.company_name||''), ...(t.companies||[]).map(c=>c.name||'')]).map(s=>s.toLowerCase()).filter(Boolean);
      const taskContacts = tasks.flatMap(t => (t.contacts || []).map(c => `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase()));
      // Suppliers are linked at project level (project_suppliers → companies)
      const projSuppliers = (proj.suppliers || []).map(s => (s.name || '').toLowerCase()).filter(Boolean);

      switch (searchScope) {
        case 'projects':
          return projName.includes(q) || taskTitles.some(t => t.includes(q));
        case 'company':
          return projCompanies.some(c => c.includes(q)) || taskCompanies.some(c => c.includes(q));
        case 'suppliers':
          return projSuppliers.some(s => s.includes(q));
        case 'contacts':
          return projContacts.some(c => c.includes(q)) || taskContacts.some(c => c.includes(q));
        case 'users':
          return (proj.contact_name || '').toLowerCase().includes(q) ||
            tasks.some(t => (t.assigned_to_name || '').toLowerCase().includes(q));
        default:
          return projName.includes(q) || taskTitles.some(t => t.includes(q));
      }
    });
  }

  // When searching: deduplicate tasks by task ID across all projects/row groups
  // A task matching in multiple projects should only appear once (first occurrence wins)
  if (searchQuery.trim()) {
    const seenTaskIds = new Set();
    filteredProjects = filteredProjects.reduce((acc, proj) => {
      const uniqueTasks = (proj.tasks || []).filter(t => {
        const key = String(t.id);
        if (seenTaskIds.has(key)) return false;
        seenTaskIds.add(key);
        return true;
      });
      if (uniqueTasks.length > 0) {
        acc.push({ ...proj, tasks: uniqueTasks });
      }
      return acc;
    }, []);
  }

  // Apply text scale to document root so modals/portals inherit it
  useEffect(() => {
    document.documentElement.style.zoom = textScale !== 1.0 ? textScale : '';
    return () => { document.documentElement.style.zoom = ''; };
  }, [textScale]);

  return (
    <Flex direction="column" h="100%" bg="gray.50" overflow="hidden">
      {/* Fixed Header */}
      <Flex
        bg="white"
        borderBottom="1px"
        borderColor="gray.200"
        px={4}
        h="48px"
        minH="48px"
        align="center"
        justify="space-between"
        zIndex={100}
        boxShadow="sm"
        flexShrink={0}
      >
        <Flex align="center" gap={3}>
          <Heading size="sm" color="brand.700" fontWeight="700" letterSpacing="-0.02em">
            ForgeBOS
          </Heading>
          <Badge colorScheme="teal" fontSize="10px" variant="subtle">
            v{APP_VERSION}
          </Badge>
        </Flex>

        {/* Search bar */}
        <Flex align="center" flex={1} maxW="500px" mx={3} gap={1}>
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none">
              <Icon as={FiSearch} color="gray.400" boxSize={3} />
            </InputLeftElement>
            <Input pl={7} pr={1} placeholder="Search..." fontSize="11px" borderRadius="md"
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              borderColor="gray.200" _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }} />
          </InputGroup>
          <Select size="sm" w="140px" fontSize="10px" flexShrink={0} value={searchScope}
            onChange={e => setSearchScope(e.target.value)} borderColor="gray.200">
            <option value="projects">Projects &amp; Tasks</option>
            <option value="company">Company</option>
            <option value="suppliers">Suppliers</option>
            <option value="contacts">Contacts</option>
            <option value="users">Users</option>
          </Select>
          <Button size="sm" variant={searchAllRows ? 'outline' : 'solid'} fontSize="9px" flexShrink={0}
            colorScheme={searchAllRows ? 'gray' : 'orange'} px={2} whiteSpace="nowrap"
            onClick={() => setSearchAllRows(!searchAllRows)}
            title={searchAllRows ? 'Searching all rows — click to restrict to current view' : 'Searching current view only — click to search all rows'}>
            Search Filtered Row Groups
          </Button>
        </Flex>

        {/* View & Filter Icons */}
        <HStack spacing={1}>
          <Tooltip label={`View: ${activeViewName}`} fontSize="xs">
            <IconButton icon={<FiEye size={16} />} size="sm" variant="ghost"
              color={activeViewId ? 'blue.500' : 'gray.500'}
              onClick={viewSelectorDisclosure.onOpen} aria-label="Views" />
          </Tooltip>
          <Tooltip label={engagementFilter.length > 0 ? `Filtering ${engagementFilter.length} statuses` : 'Filter by engagement'} fontSize="xs">
            <Box position="relative" display="inline-block">
              <IconButton icon={<FiActivity size={16} />} size="sm" variant="ghost"
                color={engagementFilter.length > 0 ? '#EE7203' : 'gray.500'}
                onClick={engFilterDisclosure.onOpen} aria-label="Engagement Filter" />
              {engagementFilter.length > 0 && (
                <Badge position="absolute" top="-2px" right="-2px" colorScheme="orange" borderRadius="full"
                  fontSize="7px" minW="14px" textAlign="center">{engagementFilter.length}</Badge>
              )}
            </Box>
          </Tooltip>
          <Tooltip label={hasActiveFilters ? 'Filters active' : 'Filters'} fontSize="xs">
            <IconButton icon={<FiSliders size={16} />} size="sm" variant="ghost"
              color={hasActiveFilters ? '#EE7203' : 'gray.500'}
              onClick={dateFilterDisclosure.onOpen} aria-label="Filters" />
          </Tooltip>
          <Tooltip label="Dashboard Column Settings" fontSize="xs">
            <IconButton icon={<FiColumns size={16} />} size="sm" variant="ghost"
              color="gray.500" onClick={columnsDisclosure.onOpen} aria-label="Columns" />
          </Tooltip>
        </HStack>

        <Flex align="center" gap={2}>
          {/* Power Dashboards — animated custom dropdown */}
          <PowerDashMenu sections={[
            { label: 'Actions', items: [
              // Routes to the new full-page TODO at /todo. The old
              // ToDoListsDashboard modal is kept rendered below for any
              // residual openers (e.g. quick-action keyboard shortcut), but
              // the menu entry now drives users to the full page.
              ...(hasFeature('todo') ? [{ icon: FiCheckSquare, label: 'To Do Lists', onClick: () => { setCurrentPage('todo'); navigate('/todo'); } }] : []),
            ]},
            { label: 'Sales', items: [
              ...(hasFeature('prospecting') ? [{ icon: FiSearch, label: 'Prospecting', onClick: () => { setCurrentPage('prospecting'); navigate('/prospecting'); } }] : []),
              ...(hasFeature('pipeline') ? [{ icon: FiTrendingUp, label: 'Business Pipeline', onClick: () => setCurrentPage('pipeline') }] : []),
              ...(hasFeature('companies') ? [{ icon: FiBriefcase, label: 'Companies', onClick: () => companiesDisclosure.onOpen() }] : []),
              ...(hasFeature('contacts') ? [{ icon: FiUsers, label: 'Contacts', onClick: () => contactsDisclosure.onOpen() }] : []),
              ...(hasFeature('engagement') ? [{ icon: FiMail, label: 'View Engagement', onClick: () => engagementDisclosure.onOpen() }] : []),
              ...(hasFeature('book_diary') ? [{ icon: FiCalendar, label: 'Book My Diary', onClick: () => bookDiaryDisclosure.onOpen() }] : []),
              { icon: FiRefreshCw, label: 'Subscriptions', onClick: () => subscriptionsDisclosure.onOpen() },
              { icon: FiUsers, label: 'Referrals', onClick: () => referralsDisclosure.onOpen() },
            ]},
            { label: 'Products', items: [
              ...(hasFeature('products') ? [{ icon: FiPackage, label: 'Products & Services', onClick: () => setCurrentPage('products') }] : []),
              ...(hasFeature('supplier_products') ? [{ icon: FiShoppingCart, label: 'Supplier Products & Services', onClick: () => setCurrentPage('supplierProducts') }] : []),
              ...(hasFeature('resources') ? [{ icon: FiPackage, label: 'Resources', onClick: () => bookingsDashDisclosure.onOpen() }] : []),
            ]},
            { label: 'Accounts', items: [
              ...(hasFeature('invoice_intake') ? [{ icon: FiUpload, label: 'Upload Invoices', onClick: () => navigate('/accounts/invoices') }] : []),
              ...(hasFeature('vat_ledger') ? [{ icon: FiPercent, label: 'VAT', onClick: () => navigate('/accounts/vat') }] : []),
              ...(hasFeature('bookkeeper') ? [{ icon: FiDollarSign, label: 'Bookkeeper', onClick: () => bookKeeperEntryDisclosure.onOpen() }] : []),
              ...(hasFeature('bank_ledger') ? [{ icon: FiDollarSign, label: 'Bank Account Ledger', onClick: () => bankLedgerDisclosure.onOpen() }] : []),
              { icon: FiCreditCard, label: 'Payment Cards',
                disabled: !['admin','accounts_manager'].includes(user?.role),
                disabledReason: 'Admin or Accounts Manager role required',
                onClick: () => setCurrentPage('paymentCards') },
              ...(hasFeature('nominal_codes') ? [{ icon: FiList, label: 'Nominal Codes', onClick: () => setCurrentPage('nominalCodes') }] : []),
              ...(hasFeature('mileage') ? [{ icon: FiTruck, label: 'Mileage Reports', onClick: () => setCurrentPage('mileage') }] : []),
            ]},
            { label: 'HR', items: [
              ...(hasFeature('departments') ? [{ icon: FiUsers, label: 'Departments', onClick: () => departmentsDisclosure.onOpen() }] : []),
              ...(hasFeature('annual_leave') ? [{ icon: FiCalendar, label: 'Annual Leave', onClick: () => holidayDisclosure.onOpen() }] : []),
              ...(hasFeature('timesheets') ? [{ icon: FiClock, label: 'Timesheets', onClick: () => timesheetsDisclosure.onOpen() }] : []),
              ...(user?.role === 'admin' && hasFeature('payroll') ? [{ icon: FiDollarSign, label: 'Payroll', onClick: () => setCurrentPage('salary') }] : []),
            ]},
            { label: 'Admin', items: [
              ...(hasFeature('procedures') ? [{ icon: FiClipboard, label: 'Procedures', onClick: () => setCurrentPage('procedures') }] : []),
              ...(hasFeature('password_safe') ? [{ icon: FiKey, label: 'Password Safe', onClick: () => setCurrentPage('passwordSafe') }] : []),
              ...(hasFeature('site_visitors') ? [{ icon: FiUsers, label: 'Site Visitors', onClick: () => siteVisitorsDisclosure.onOpen() }] : []),
              { icon: FiAlertCircle, label: 'Incident Log', onClick: () => incidentDashDisclosure.onOpen() },
              { icon: FiMail, label: 'Email Filters', onClick: () => emailFiltersDisclosure.onOpen() },
            ]},
          ].filter(s => s.items.length > 0)} />

          <Box position="relative" display="inline-block">
            <Button size="sm" variant="ghost" leftIcon={<FiCalendar />} fontSize="12px"
              onClick={calendarDashDisclosure.onOpen} color="gray.600"
              onContextMenu={(e) => { e.preventDefault(); setScheduleMenu({ x: e.clientX, y: e.clientY }); }}>
              Schedule
            </Button>
          </Box>

          <Button size="sm" variant="ghost" leftIcon={<FiBookOpen />} fontSize="12px"
            onClick={bookingsDashDisclosure.onOpen} color="gray.600">
            Bookings
          </Button>

          {/* Inbox button with unread pulse */}
          <Box position="relative" display="inline-block">
            <IconButton
              icon={<FiMail size={18} />}
              size="sm"
              variant="ghost"
              onClick={inboxDisclosure.onOpen}
              aria-label="Inbox"
              color={inboxUnread > 0 ? '#EE7203' : 'gray.600'}
              sx={inboxUnread > 0 ? {
                animation: 'emailPulse 2s ease-in-out infinite',
                '@keyframes emailPulse': {
                  '0%, 100%': { color: '#c05600' },
                  '50%': { color: '#EE7203' },
                },
              } : {}}
            />
            {inboxUnread > 0 && (
              <Badge position="absolute" top="-2px" right="-4px" colorScheme="red" borderRadius="full"
                fontSize="8px" minW="16px" textAlign="center">{inboxUnread}</Badge>
            )}
          </Box>

          <Tooltip label="Settings" fontSize="xs">
            <IconButton
              icon={<FiSettings />}
              size="sm"
              variant="ghost"
              onClick={settingsDisclosure.onOpen}
              aria-label="Settings"
            />
          </Tooltip>

          <Flex align="center" gap={2} ml={2} pl={2} borderLeft="1px" borderColor="gray.200">
            <FiUser size={14} />
            <Text fontSize="12px" fontWeight="500">{user?.display_name || user?.username}</Text>
            <Button size="sm" variant="ghost" leftIcon={<FiLogOut />} fontSize="12px" color="gray.600" onClick={logout} aria-label="Log out">
              Log out
            </Button>
          </Flex>
        </Flex>
      </Flex>

      {/* Main Content — grid or full-page dashboard */}
      {currentPage ? (
        <Box flex={1} overflowY="auto" overflowX="auto">
          {currentPage === 'prospecting' && <ProspectingDashboard fullPage onBack={() => { setCurrentPage(null); navigate('/'); }} />}
        {subscriptionsDisclosure.isOpen && <Box position="fixed" inset={0} bg="white" zIndex={1100} overflowY="auto"><SubscriptionsDashboard isOpen={subscriptionsDisclosure.isOpen} fullPage onBack={subscriptionsDisclosure.onClose} onClose={subscriptionsDisclosure.onClose}/></Box>}
        {referralsDisclosure.isOpen && <Box position="fixed" inset={0} bg="white" zIndex={1100} overflowY="auto"><ReferralsDashboard isOpen={referralsDisclosure.isOpen} fullPage onBack={referralsDisclosure.onClose} onClose={referralsDisclosure.onClose}/></Box>}
          {currentPage === 'salary' && <SalaryDashboard onBack={() => setCurrentPage(null)} onOpenPension={() => setCurrentPage('pension')} onOpenEmployeeTax={() => setCurrentPage('employee-tax')} />}
          {currentPage === 'pension' && <PensionDashboard onBack={() => setCurrentPage('salary')} />}
          {currentPage === 'employee-tax' && <EmployeeTaxDashboard onBack={() => setCurrentPage('salary')} />}
          {currentPage === 'pipeline' && <BusinessPipelineDashboard fullPage onBack={() => setCurrentPage(null)} />}
          {currentPage === 'bookkeeper' && <AccountingModal fullPage defaultDir={bookKeeperDir} defaultStatusOut={bookKeeperDir === 'out' ? 'to_invoice' : ''} onBack={() => { setCurrentPage(null); setBookKeeperDir(''); }} />}
          {currentPage === 'mileage' && <MileageReportsModal fullPage onBack={() => setCurrentPage(null)} />}
          {currentPage === 'products' && <ProductsModal fullPage onBack={() => setCurrentPage(null)} />}
          {currentPage === 'supplierProducts' && <SupplierProductsModal fullPage onBack={() => setCurrentPage(null)} />}
          {currentPage === 'nominalCodes' && <NominalCodesModal fullPage onBack={() => setCurrentPage(null)} />}
          {currentPage === 'upload-portals' && uploadPortalCtx && <UploadPortalsPage fullPage
            taskId={uploadPortalCtx.taskId} subTaskId={uploadPortalCtx.subTaskId} contextName={uploadPortalCtx.name}
            onBack={() => { setCurrentPage(null); setUploadPortalCtx(null); }} />}
          {currentPage === 'procedures' && <ProceduresDashboard fullPage
            initialEditId={procDeepLink?.editId} initialViewId={procDeepLink?.viewId}
            onBack={() => { setCurrentPage(null); setProcDeepLink(null); }} />}
          {currentPage === 'passwordSafe' && <PasswordSafeDashboard fullPage onBack={() => setCurrentPage(null)} />}
          {currentPage === 'invoiceIntake' && <InvoiceIntakeDashboard onBack={() => navigate('/')} />}
          {currentPage === 'vat' && <VatDashboard onBack={() => navigate('/')} />}
          {currentPage === 'paymentCards' && <PaymentCardsPage fullPage onBack={() => setCurrentPage(null)} />}
          {currentPage === 'todo' && (
            <Box p={3}>
              <ToDoListPage
                onBack={() => { setCurrentPage(null); navigate('/'); }}
                // Mirrors the InboxModal "go to project" flow: reveal only the
                // row group containing the project, clear any current search,
                // and scroll the project row into view once the grid renders.
                onNavigateToProject={(projectId, rowGroupId, taskId) => {
                  if (rowGroupId) {
                    setRowGroups(prev => prev.map(g => ({
                      ...g,
                      visibility: g.id === rowGroupId ? 'show' : 'hide',
                      visible: g.id === rowGroupId,
                    })));
                  }
                  setSearchQuery('');
                  setCurrentPage(null);
                  navigate('/');
                  if (taskId && projectId) {
                    // Open the comms modal for that task once the grid is up
                    setAutoOpenComms({ projectId, taskId, ts: Date.now() });
                    setTimeout(() => setAutoOpenComms(null), 5000);
                  } else if (taskId) {
                    setHighlightTaskId(taskId);
                    setTimeout(() => setHighlightTaskId(null), 8000);
                  }
                  setTimeout(() => {
                    const el = document.querySelector(`[data-project-id="${projectId}"]`);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 250);
                }}
              />
            </Box>
          )}
          {currentPage?.startsWith('projectFocus:') && (
            <ProjectFocusDashboard
              projectId={currentPage.split(':')[1]}
              onBack={() => setCurrentPage(null)}
            />
          )}
        </Box>
      ) : (
        <Box flex={1} overflowY="auto" overflowX="auto" px={2} pt={0} pb={globalActiveSession ? 16 : 2}>
          <MainGrid
            projects={filteredProjects}
            rowGroups={searchQuery.trim()
              ? (searchAllRows
                  ? rowGroups.filter(g => filteredProjects.some(p => {
                      const pGroups = p.row_groups?.length > 0 ? p.row_groups : (p.row_group_id ? [{ row_group_id: p.row_group_id }] : []);
                      return pGroups.some(rg => rg.row_group_id === g.id);
                    }))
                  : visibleGroups.filter(g => filteredProjects.some(p => {
                      const pGroups = p.row_groups?.length > 0 ? p.row_groups : (p.row_group_id ? [{ row_group_id: p.row_group_id }] : []);
                      return pGroups.some(rg => rg.row_group_id === g.id);
                    })))
              : visibleGroups}
            allRowGroups={rowGroups}
            loading={loading}
            onUpdateProject={handleUpdateProject}
            onDeleteProject={handleDeleteProject}
            onAddProject={handleAddProject}
            onRefresh={fetchData}
            taskUnread={taskUnread}
            highlightTaskId={highlightTaskId}
            autoOpenComms={autoOpenComms}
            onOpenInvoiceIntake={(pid, tid) => { navigate(`/accounts/invoices/single?task=${encodeURIComponent(tid)}`); }}
            onOpenBookkeeper={(dir) => {
              setBookKeeperDir(dir || '');
              setCurrentPage('bookkeeper');
            }}
            onFocusProject={(projectId) => setCurrentPage(`projectFocus:${projectId}`)}
          />
        </Box>
      )}

      {/* Settings Drawer */}
      <SettingsDrawer
        isOpen={settingsDisclosure.isOpen}
        onClose={settingsDisclosure.onClose}
        rowGroups={rowGroups}
        onRefresh={() => { fetchData(); fetchViewFilters(); }}
        onApplyView={applyViewFilter}
        textScale={textScale}
        onTextScaleChange={updateTextScale}
      />

      <EngagementModal isOpen={engagementDisclosure.isOpen} onClose={engagementDisclosure.onClose} />
      <CompaniesListModal isOpen={companiesDisclosure.isOpen} onClose={companiesDisclosure.onClose} />
      <ContactsListModal isOpen={contactsDisclosure.isOpen} onClose={contactsDisclosure.onClose} />
      <CalendarDashboardModal isOpen={calendarDashDisclosure.isOpen} onClose={calendarDashDisclosure.onClose} />
      <ResourceBookingsDashboard isOpen={bookingsDashDisclosure.isOpen} onClose={bookingsDashDisclosure.onClose} onManageResources={() => { bookingsDashDisclosure.onClose(); resourcesDisclosure.onOpen(); }} />
      {siteVisitorsDisclosure.isOpen && (
        <Box position="fixed" inset={0} bg="white" zIndex={1200} overflowY="auto">
          <SiteVisitorsDashboard onBack={siteVisitorsDisclosure.onClose} />
        </Box>
      )}
      {bookDiaryDisclosure.isOpen && (
        <Box position="fixed" inset={0} bg="white" zIndex={1200} overflowY="auto">
          <BookMyDiaryDashboard onBack={bookDiaryDisclosure.onClose} />
        </Box>
      )}
      <ToDoListsDashboard isOpen={todoDisclosure.isOpen} onClose={todoDisclosure.onClose}
        onNavigateToProject={(rowGroupId, projectId) => {
          setCurrentPage(null);
          setSearchQuery('');
          // Scroll to project row after a tick
          setTimeout(() => {
            const el = document.querySelector(`[data-project-id="${projectId}"]`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 200);
        }} />
      <BankLedgerDashboard isOpen={bankLedgerDisclosure.isOpen} onClose={bankLedgerDisclosure.onClose} />
      <HolidayDashboard isOpen={holidayDisclosure.isOpen} onClose={holidayDisclosure.onClose} />
      <TimesheetsDashboard isOpen={timesheetsDisclosure.isOpen} onClose={timesheetsDisclosure.onClose} />
      <IncidentDashboard isOpen={incidentDashDisclosure.isOpen} onClose={incidentDashDisclosure.onClose} />
      <EmailFiltersDashboard isOpen={emailFiltersDisclosure.isOpen} onClose={emailFiltersDisclosure.onClose} />
      <BookKeeperEntryModal
        isOpen={bookKeeperEntryDisclosure.isOpen}
        onClose={bookKeeperEntryDisclosure.onClose}
        onOpen={(mode) => {
          if (mode === 'ledger') { setBookKeeperDir(''); setCurrentPage('bookkeeper'); }
          else if (mode === 'raise') { setBookKeeperDir('out'); setCurrentPage('bookkeeper'); }
          else if (mode === 'assign') { assignMoneyDisclosure.onOpen(); }
          else if (mode === 'pay')   { setBookKeeperDir('in');  setCurrentPage('bookkeeper'); }
        }}
      />
      <AssignReceivedMoneyModal isOpen={assignMoneyDisclosure.isOpen} onClose={assignMoneyDisclosure.onClose} />

      {/* Floating active timer bar — shown when a work session is running and the modal is closed */}
      {globalActiveSession && (() => {
        const h = Math.floor(globalElapsed / 3600);
        const m = Math.floor((globalElapsed % 3600) / 60);
        const s = globalElapsed % 60;
        const elapsed = `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
        return (
          <Box
            position="fixed" bottom={0} left={0} right={0} zIndex={9000}
            bg="red.600" color="white" px={4} py={2}
            display="flex" alignItems="center" justifyContent="space-between"
            boxShadow="0 -4px 20px rgba(0,0,0,0.3)"
          >
            <Box display="flex" alignItems="center" gap={3}>
              {/* Pulsing dot */}
              <Box
                w="10px" h="10px" borderRadius="full" bg="white" flexShrink={0}
                sx={{
                  animation: 'timerPulse 1s ease-in-out infinite',
                  '@keyframes timerPulse': {
                    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                    '50%': { opacity: 0.4, transform: 'scale(0.7)' },
                  },
                }}
              />
              <Box>
                <Box display="flex" alignItems="center" gap={2}>
                  <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="wider" opacity={0.8}>
                    Timer Running
                  </Text>
                  <Text fontSize="22px" fontWeight="800" fontFamily="mono" lineHeight={1}>
                    {elapsed}
                  </Text>
                </Box>
                <Box display="flex" alignItems="center" gap={2} mt={0.5}>
                  {globalActiveSession.project_name && (
                    <Text fontSize="11px" opacity={0.9} fontWeight="600">
                      {globalActiveSession.project_name}
                    </Text>
                  )}
                  {globalActiveSession.project_name && globalActiveSession.task_title && (
                    <Text fontSize="11px" opacity={0.6}>›</Text>
                  )}
                  {globalActiveSession.task_title && (
                    <Text fontSize="11px" opacity={0.9}>
                      {globalActiveSession.task_title}
                    </Text>
                  )}
                  {globalActiveSession.sub_task_title && (
                    <>
                      <Text fontSize="11px" opacity={0.6}>›</Text>
                      <Text fontSize="11px" opacity={0.8} fontStyle="italic">
                        {globalActiveSession.sub_task_title}
                      </Text>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={3}>
              {/* Attributed user */}
              {globalActiveSession.user_name && (
                <Box textAlign="right">
                  <Text fontSize="9px" opacity={0.7} textTransform="uppercase" letterSpacing="wide">Logged to</Text>
                  <Text fontSize="12px" fontWeight="700">{globalActiveSession.user_name}</Text>
                </Box>
              )}
              {/* Click to open the work session modal for this task */}
              <Box
                as="button"
                bg="white" color="red.600" fontWeight="700" fontSize="11px"
                px={3} py={1} borderRadius="md"
                _hover={{ bg: 'red.50' }}
                onClick={() => {
                  if (globalActiveSession.task_id) {
                    setFloatingTimerTask({
                      taskId: globalActiveSession.task_id,
                      taskTitle: globalActiveSession.task_title || '',
                      projectId: globalActiveSession.project_id,
                    });
                  }
                }}
              >
                Open
              </Box>
            </Box>
          </Box>
        );
      })()}

      {/* Work session modal opened from floating timer bar */}
      {floatingTimerTask && (
        <WorkSessionModal
          taskId={floatingTimerTask.taskId}
          taskTitle={floatingTimerTask.taskTitle}
          projectId={floatingTimerTask.projectId}
          isOpen={true}
          onClose={() => setFloatingTimerTask(null)}
          onRefresh={fetchData}
        />
      )}

      <InboxModal
        isOpen={inboxDisclosure.isOpen}
        onClose={inboxDisclosure.onClose}
        onRefreshCounts={fetchUnreadCounts}
        onNavigateToProject={(projectId, rowGroupId, taskId) => {
          // Reveal the row group that contains this project
          if (rowGroupId) {
            setRowGroups(prev => prev.map(g => ({
              ...g,
              visibility: g.id === rowGroupId ? 'show' : 'hide',
              visible: g.id === rowGroupId,
            })));
          }
          setSearchQuery('');
          inboxDisclosure.onClose();
          if (taskId && projectId) {
            // Auto-open the communications modal for this task after grid renders
            setAutoOpenComms({ projectId, taskId, ts: Date.now() });
            // Clear after a few seconds so it doesn't re-trigger
            setTimeout(() => setAutoOpenComms(null), 5000);
          } else if (taskId) {
            setHighlightTaskId(taskId);
            setTimeout(() => setHighlightTaskId(null), 8000);
          }
        }}
      />

      <ViewSelectorModal
        isOpen={viewSelectorDisclosure.isOpen}
        onClose={viewSelectorDisclosure.onClose}
        activeViewId={activeViewId}
        onApply={(view) => {
          if (view === '__show_all__') {
            // Mark "show all" as the active selection so it survives the
            // post-action refetch in fetchData. Previously activeFilterRef
            // was nulled here — meaning the next refetch fell back to
            // server defaults and the user's "show all" choice was lost.
            activeFilterRef.current = '__show_all__';
            setRowGroups(prev => prev.map(g => ({ ...g, visibility: 'show', visible: true })));
            setActiveViewId('__show_all__');
            setActiveViewName('Show All');
            try { sessionStorage.setItem('forgebos_active_filter_id', '__show_all__'); } catch(e) {}
          } else if (view) {
            applyViewFilter(view);
          } else {
            activeFilterRef.current = null; fetchData(); setActiveViewId(null); setActiveViewName('Default');
            try { sessionStorage.removeItem('forgebos_active_filter_id'); } catch(e) {}
          }
        }}
      />

      <EngagementFilterModal
        isOpen={engFilterDisclosure.isOpen}
        onClose={engFilterDisclosure.onClose}
        activeFilter={engagementFilter}
        onApply={setEngagementFilter}
      />

      <NationalHolidaysModal isOpen={nationalHolidaysDisclosure.isOpen} onClose={nationalHolidaysDisclosure.onClose} />

      <ProspectsModal
        isOpen={prospectsDisclosure.isOpen}
        onClose={prospectsDisclosure.onClose}
        onRefresh={fetchData}
      />

      {/* Filters Modal */}
      <Modal isOpen={dateFilterDisclosure.isOpen} onClose={dateFilterDisclosure.onClose} size="sm" isCentered>
        <ModalOverlay /><ModalContent>
          <ModalHeader fontSize="md">Filters</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3}>
              <FormControl>
                <FormLabel fontSize="xs" mb={1}>From Date</FormLabel>
                <Input size="sm" type="date" value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" mb={1}>To Date</FormLabel>
                <Input size="sm" type="date" value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)} />
              </FormControl>
              <Divider />
              <HStack w="100%" spacing={3}>
                <FormControl>
                  <FormLabel fontSize="xs" mb={1}>Min Priority</FormLabel>
                  <Select size="sm" value={priorityMin} onChange={(e) => setPriorityMin(parseInt(e.target.value))}>
                    {Array.from({ length: 11 }, (_, i) => (
                      <option key={i} value={i}>{i}{i === 0 ? ' (Lowest)' : i === 5 ? ' (Normal)' : i === 10 ? ' (Urgent)' : ''}</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" mb={1}>Max Priority</FormLabel>
                  <Select size="sm" value={priorityMax} onChange={(e) => setPriorityMax(parseInt(e.target.value))}>
                    {Array.from({ length: 11 }, (_, i) => (
                      <option key={i} value={i}>{i}{i === 0 ? ' (Lowest)' : i === 5 ? ' (Normal)' : i === 10 ? ' (Urgent)' : ''}</option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>
              <Divider />
              <Checkbox size="sm" isChecked={showDeletedProjects} onChange={e => setShowDeletedProjects(e.target.checked)}>
                <Text fontSize="11px">Show Deleted Projects</Text>
              </Checkbox>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={2}>
              <Button size="sm" variant="ghost"
                onClick={() => {
                  setDateFrom(''); setDateTo(new Date().toISOString().split('T')[0]);
                  setPriorityMin(0); setPriorityMax(10);
                  api.put('/auth/preferences', { filter_priority_min: 0, filter_priority_max: 10 }).catch(() => {});
                  dateFilterDisclosure.onClose();
                }}>
                Reset All
              </Button>
              <Button size="sm" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
                onClick={() => {
                  api.put('/auth/preferences', { filter_priority_min: priorityMin, filter_priority_max: priorityMax }).catch(() => {});
                  dateFilterDisclosure.onClose();
                }}>
                Apply
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Schedule right-click alarm mode menu */}
      {scheduleMenu && (
        <Box position="fixed" left={`${scheduleMenu.x}px`} top={`${scheduleMenu.y}px`} bg="white" border="1px"
          borderColor="gray.200" borderRadius="md" boxShadow="lg" zIndex={9999} py={1} minW="260px">
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }}
            fontWeight={alarmMode === 'all' ? '700' : '400'} color={alarmMode === 'all' ? 'green.600' : undefined}
            onClick={() => changeAlarmMode('all')}>
            {alarmMode === 'all' ? '✓ ' : ''}Alarm everything
          </Box>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }}
            fontWeight={alarmMode === 'calendar_only' ? '700' : '400'} color={alarmMode === 'calendar_only' ? 'blue.600' : undefined}
            onClick={() => changeAlarmMode('calendar_only')}>
            {alarmMode === 'calendar_only' ? '✓ ' : ''}Allow calendar appointments with alarms
          </Box>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'red.50' }}
            fontWeight={alarmMode === 'silence' ? '700' : '400'} color={alarmMode === 'silence' ? 'red.500' : undefined}
            onClick={() => changeAlarmMode('silence')}>
            {alarmMode === 'silence' ? '✓ ' : ''}Silence all alarms
          </Box>
        </Box>
      )}

      {/* Global Alarm Popup */}
      <AlarmPopup />
      <DashboardColumnsModal isOpen={columnsDisclosure.isOpen} onClose={columnsDisclosure.onClose} />
      <DepartmentsModal isOpen={departmentsDisclosure.isOpen} onClose={departmentsDisclosure.onClose} />
      <ResourcesModal isOpen={resourcesDisclosure.isOpen} onClose={resourcesDisclosure.onClose} />
    </Flex>
  );
}
